import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Brain, Copy, Star, Trash2, EyeOff, Sparkles, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BRAND_MODERATION_PAGE_SIZE, ConsumerResearchApiFactory, QueueType, type BrandModerationQueueListItem, type GenerateResponseParams } from "@/apis/research/consumer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { AutoresizeTextarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn, formatDate } from "@/lib/utils";
import { type ModerationFeedCard } from "@/mock/ai-consumer-research";
import { PlaybooksApiFactory, type UpdatePlaybookRequest } from "@/apis/research/playbooks";

const statusStyles: Record<string, { label: string; className: string }> = {
  all: { label: "All", className: "text-foreground" },
  review_needed: { label: "Needs Review", className: "text-warning" },
  auto_responded: { label: "Auto-responded", className: "text-success" },
  flag: { label: "Flagged", className: "text-destructive" },
};

const priorityStyles: Record<ModerationFeedCard["priority"], string> = {
  high: "text-destructive",
  medium: "text-warning",
  low: "text-muted-foreground",
};

function titleCasePlatform(platform: string) {
  if (!platform) return "";
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
}

function mapApiPriority(priority: string): ModerationFeedCard["priority"] {
  const p = priority.toLowerCase();
  if (p === "high") return "high";
  if (p === "low") return "low";
  return "medium";
}

function mapBrandModerationQueueItemToFeedCard(item: BrandModerationQueueListItem): ModerationFeedCard {
  const handle = item.comment_user_name.startsWith("@") ? item.comment_user_name : `@${item.comment_user_name}`;
  const confidencePct = Math.min(100, Math.max(0, Math.round(item.confidence_score * 100)));
  const categoryLabel = item.category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return {
    platform: titleCasePlatform(item.platform),
    subBrand: item.brand_name,
    sourceDescription: categoryLabel,
    userHandle: handle,
    rating: 0,
    content: item.comment_text,
    rule: item.matched_rule_summary,
    confidence: confidencePct,
    status: item.queue_type as QueueType,
    priority: mapApiPriority(item.priority),
    lastSync: formatDate(item.updated_at, "MMM d, yyyy · h:mm a") || "—",
    whyReason: item.matched_rule_summary.trim() ? item.matched_rule_summary : categoryLabel,
    trigger: item.matched_rule_summary,
    playbookApplied: item.playbook_name || "—",
    aiDraft: item.ai_draft_response ?? "",
    aiResponse: false,
    replyType: "sent_after_review",
    ruleFilter: "all",
    actionTaken: "FLAG",
  };
}

function ApiQueueCardList({ items }: { items: BrandModerationQueueListItem[] }) {
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <ReviewCard key={item.id} moderationQueueId={item.id} flagItem={item} card={mapBrandModerationQueueItemToFeedCard(item)} />
      ))}
    </div>
  );
}

function TabEmpty({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{message}</p>;
}

function ReviewQueueCardSkeleton() {
  return (
    <Card className="rounded-xl border shadow-none overflow-hidden">
      <CardContent className="space-y-3 p-0 px-4 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-24 rounded-md" />
            <Skeleton className="h-3 w-28 max-w-[45%]" />
            <Skeleton className="h-3 w-14 shrink-0" />
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
            <Skeleton className="h-5 w-[4.25rem] rounded px-1.5" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
        <div className="space-y-2 pb-0.5">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[96%]" />
          <Skeleton className="h-4 w-[72%]" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pb-0.5">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-[4.5rem] rounded-sm" />
            <Skeleton className="h-3 w-36 max-w-[55vw]" />
          </div>
          <Skeleton className="h-3 w-14" />
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewQueueListSkeleton({ count = 4, label }: { count?: number; label: string }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <ReviewQueueCardSkeleton key={i} />
      ))}
    </div>
  );
}

type ModerationPageMeta = {
  page: number;
  size: number;
  total: number;
  pages: number;
};

function ModerationPaginationBar({ meta, isFetching, onPageChange }: { meta: ModerationPageMeta | undefined; isFetching: boolean; onPageChange: (page: number) => void }) {
  if (!meta || meta.total === 0) return null;

  const { page, pages } = meta;
  const totalPages = Math.max(1, pages);
  const canPrev = page > 1 && !isFetching;
  const canNext = page < totalPages && !isFetching;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 mt-2">
      <div className="flex items-center justify-center sm:justify-end gap-2 ml-auto">
        <Button variant="outline" size="sm" className="rounded-md text-xs" disabled={!canPrev} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <span className="text-xs text-muted-foreground tabular-nums min-w-[5.5rem] text-center">
          Page {page} of {totalPages}
        </span>
        <Button variant="outline" size="sm" className="rounded-md text-xs" disabled={!canNext} onClick={() => onPageChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

const REVIEW_QUEUE_SECONDARY_TABS = [
  { id: "all" as const, label: "All" },
  { id: "review_needed" as const, label: "Needs Review" },
  // { id: "auto_responded" as const, label: "Auto-Responded" },
  { id: "flag" as const, label: "Flagged" },
];

export function ReviewQueue() {
  const [queuePage, setQueuePage] = useState(1);
  const [activeTab, setActiveTab] = useState<QueueType>("review_needed");

  useEffect(() => {
    setQueuePage(1);
  }, [activeTab]);

  const {
    data: reviewQueueResponse,
    isLoading: reviewQueueLoading,
    isFetching: reviewQueueFetching,
  } = useQuery({
    ...ConsumerResearchApiFactory.Queries.ReviewQueue({ page: queuePage, size: BRAND_MODERATION_PAGE_SIZE, queue_type: activeTab }),
    placeholderData: keepPreviousData,
  });

  const queueItems = reviewQueueResponse?.data.items ?? [];
  const queueMeta = reviewQueueResponse?.data;

  return (
    <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as QueueType)}>
      <div className="overflow-x-auto scrollbar-hidden">
        <TabsList className="bg-transparent gap-2 p-0 flex-nowrap mx-auto w-fit">
          {REVIEW_QUEUE_SECONDARY_TABS.map(({ id, label }) => (
            <TabsTrigger
              key={id}
              value={id}
              className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-foreground data-[state=active]:text-background dark:data-[state=active]:bg-foreground data-[state=active]:border-foreground data-[state=active]:shadow-none whitespace-nowrap"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      <TabsContent value="all" className="mt-2">
        {reviewQueueLoading && !reviewQueueResponse ? (
          <ReviewQueueListSkeleton label="Loading review queue" />
        ) : (queueMeta?.total ?? 0) === 0 ? (
          <TabEmpty message="Nothing in the review queue yet." />
        ) : (
          <>
            {queueItems.length === 0 ? <TabEmpty message="No items on this page." /> : <ApiQueueCardList items={queueItems} />}
            <ModerationPaginationBar meta={queueMeta} isFetching={reviewQueueFetching} onPageChange={setQueuePage} />
          </>
        )}
      </TabsContent>

      <TabsContent value="review_needed" className="mt-2">
        <ApiQueueCardList items={queueItems} />
      </TabsContent>

      {/* <TabsContent value="auto_responded" className="mt-2">
        <CardList cards={autoRespondedSample} />
      </TabsContent> */}

      <TabsContent value="flag" className="mt-2">
        <ApiQueueCardList items={queueItems} />
      </TabsContent>
    </Tabs>
  );
}

function confidenceStyles(confidence: number): string {
  switch (true) {
    case confidence < 50:
      return "bg-destructive/10 text-destructive";
    case confidence <= 80:
      return "bg-warning/10 text-warning";
    default:
      return "bg-success/10 text-success";
  }
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={11} className={cn(i < rating ? "fill-warning text-warning" : "fill-muted text-muted")} />
      ))}
    </div>
  );
}

function RejectAndUpdatePlaybook({ moderationQueueId }: { moderationQueueId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const { mutate: updatePlaybook, isPending: updatePlaybookPending } = useMutation({
    mutationFn: (params: UpdatePlaybookRequest) => PlaybooksApiFactory.Apis.UpdatePlaybook(params),
    onSuccess: async () => {
      await PlaybooksApiFactory.Invalidations.AfterUpdatePlaybook(queryClient);
      await queryClient.invalidateQueries({ queryKey: ["consumer-research", "brand-moderation", "queue"] });
      toast.success("Playbook feedback recorded. Draft rejected.");
      setOpen(false);
      setFeedback("");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Failed to update playbook");
    },
  });

  useEffect(() => {
    setOpen(false);
    setFeedback("");
  }, [moderationQueueId]);

  const handleSubmit = () => {
    const trimmed = feedback.trim();
    if (!trimmed) {
      toast.error("Add feedback before submitting.");
      return;
    }
    if (!moderationQueueId) {
      toast.error("Missing queue item; cannot submit feedback.");
      return;
    }
    updatePlaybook({
      moderation_queue_id: moderationQueueId,
      rejection_prompt: trimmed,
    });
  };

  const handleCancel = () => {
    setOpen(false);
    setFeedback("");
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={cn("rounded-md text-xs gap-1.5 ml-auto", open ? "text-foreground" : "text-muted-foreground")}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <BookOpen className="size-4" /> Reject &amp; Update Playbook
      </Button>
      {open ? (
        <div className="w-full basis-full rounded-lg border border-amber-200/90 bg-amber-50/90 p-3 space-y-2 dark:border-amber-900/60 dark:bg-amber-950/25">
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">What should the Playbook say differently?</p>
          <AutoresizeTextarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            minRows={3}
            maxRows={12}
            placeholder="Describe the change you'd like to make to the Playbook..."
            className="text-sm text-foreground/90 leading-relaxed min-h-0 border-amber-200/80 bg-background shadow-none focus-visible:ring-amber-300/40 dark:border-amber-800/60"
            aria-label="Playbook change feedback"
          />
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <Button type="button" size="sm" variant="destructive" className="rounded-md text-xs gap-1.5" disabled={updatePlaybookPending} onClick={handleSubmit}>
              {updatePlaybookPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
              {updatePlaybookPending ? "Submitting…" : "Submit & Reject"}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="rounded-md text-xs text-muted-foreground" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function ReviewCard({ card, flagItem, moderationQueueId }: { card: ModerationFeedCard; flagItem?: BrandModerationQueueListItem; moderationQueueId?: string }) {
  const queryClient = useQueryClient();
  const [whyOpen, setWhyOpen] = useState(false);
  const [aiDraftText, setAiDraftText] = useState(card.aiDraft ?? "");
  const [draftCopied, setDraftCopied] = useState(false);
  const [flagDraftVisible, setFlagDraftVisible] = useState(false);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusStyle = statusStyles[card.status];

  const hasPropAiDraft = (card.aiDraft?.trim() ?? "").length > 0;
  const showAiDraftPanel = hasPropAiDraft || (flagItem != null && flagDraftVisible);

  const { mutate: generateResponse, isPending: generateResponsePending } = useMutation({
    mutationFn: (params: GenerateResponseParams) => ConsumerResearchApiFactory.Apis.GenerateResponse(params),
    onSuccess: (res) => {
      const text = res.data?.response_text ?? "";
      setAiDraftText(text);
      setFlagDraftVisible(true);
      if (!text.trim()) toast.warning("No response text returned");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Failed to generate response");
    },
  });

  const { mutate: removeQueueItem, isPending: removeQueueItemPending } = useMutation({
    mutationFn: (queueItemId: string) => ConsumerResearchApiFactory.Apis.RemoveQueueItem(queueItemId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["consumer-research", "brand-moderation", "queue"] });
      toast.success("Removed from queue");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Failed to remove from queue");
    },
  });

  useEffect(() => {
    setAiDraftText(card.aiDraft ?? "");
    setDraftCopied(false);
  }, [card.aiDraft]);

  useEffect(() => {
    setFlagDraftVisible(false);
  }, [flagItem?.id]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  const isAutoResponded = card.status === "auto_respond";
  const isFlagged = card.status === "flag";

  const handleCopyAiDraftToClipboard = () => {
    void navigator.clipboard.writeText(aiDraftText).then(() => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
      setDraftCopied(true);

      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setDraftCopied(false);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
    });
  };

  const handleGenerateFlaggedResponse = () => {
    if (!flagItem) return;
    generateResponse({
      platform: flagItem.platform,
      comment_id: flagItem.comment_id,
      comment_text: flagItem.comment_text,
      post_id: flagItem.post_id,
      post_text: flagItem.post_text,
      comment_user_name: flagItem.comment_user_name,
    });
  };

  return (
    <Card className="rounded-xl border shadow-none overflow-hidden">
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 px-4">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-sm text-foreground">{card.platform}</span>
            <Badge className="rounded-md bg-blue-50 text-blue-600 text-xs font-medium px-2 py-0.5 border-0">{card.sourceDescription}</Badge>
            <span className="text-xs text-muted-foreground truncate">{card.userHandle}</span>
            <StarRating rating={card.rating} />
          </div>
          <div className="flex items-center gap-3 shrink-0 text-xs">
            <span className={cn("font-semibold rounded px-1.5 py-0.5 text-xs", confidenceStyles(card.confidence))}>{card.confidence}% conf</span>
            <span className={cn("font-semibold", statusStyle.className)}>{statusStyle.label}</span>
            <span className={cn("font-bold uppercase", priorityStyles[card.priority])}>{card.priority}</span>
            <span className="text-muted-foreground">{card.lastSync}</span>
          </div>
        </div>

        <div className="px-4 pb-3">
          <p className="text-sm text-foreground leading-relaxed">{card.content}</p>
        </div>

        <div className="px-4 pb-3">
          <Collapsible open={whyOpen} onOpenChange={setWhyOpen}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-sm text-xs font-medium px-2 py-0.5">
                  {card.subBrand}
                </Badge>
                {card.rule ? <span className="text-xs text-muted-foreground">Rule: {card.rule}</span> : null}
              </div>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <Brain className="size-4" />
                  Why?
                  <ChevronDown className={cn("size-4 transition-transform", whyOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent>
              <div className="mt-3 rounded-lg bg-muted/40 px-4 py-3 space-y-4 text-xs">
                <div>
                  <p className="font-medium uppercase tracking-wide text-muted-foreground mb-0.5">Why this is here</p>
                  <p className="text-foreground/80 text-sm">{card.whyReason}</p>
                </div>
                {card.trigger ? (
                  <div>
                    <p className="font-medium uppercase tracking-wide text-muted-foreground mb-0.5">Trigger</p>
                    <p className="text-foreground/80 text-sm">{card.trigger}</p>
                  </div>
                ) : null}
                <div>
                  <p className="font-medium uppercase tracking-wide text-muted-foreground mb-0.5">Playbook Applied</p>
                  <p className="text-foreground/80 text-sm">{card.playbookApplied}</p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {showAiDraftPanel ? (
          <div className={cn("mx-4 rounded-lg px-4 py-3 space-y-2 bg-background border border-blue-500", isFlagged && "mb-3")}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-foreground ">
                  <Sparkles className="size-4 text-background" />
                </div>
                <span className="text-xs font-semibold text-foreground">AI Draft</span>
                <span className="text-xs text-muted-foreground">for {card.platform}</span>
              </div>
              <Badge className="rounded-md bg-blue-100 text-blue-600 text-xs font-medium px-2 py-0.5 border-0">Awaiting review</Badge>
            </div>
            <AutoresizeTextarea
              value={aiDraftText}
              onChange={(e) => setAiDraftText(e.target.value)}
              minRows={3}
              maxRows={16}
              className="text-sm text-foreground/90 leading-relaxed min-h-0 border-blue-200/80 bg-background/90 shadow-none focus-visible:ring-blue-300/40"
              aria-label="AI draft response"
            />
            <div className="flex flex-col items-center gap-2 pt-1 md:flex-row md:flex-wrap md:items-center md:justify-start">
              <div className="flex items-center gap-2">
                <Button size="sm" className="rounded-md text-xs bg-success text-background hover:bg-success/90 gap-1.5">
                  <Check className="size-4" /> Approve &amp; Send
                </Button>
                {/* <Button size="sm" variant="outline" className="rounded-md text-xs gap-1.5">
                  <Pencil className="size-4" /> Edit
                </Button> */}
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className={cn("rounded-full text-xs px-2", draftCopied && "text-success")}
                  aria-label={draftCopied ? "Copied to clipboard" : "Copy AI draft to clipboard"}
                  onClick={handleCopyAiDraftToClipboard}
                >
                  {draftCopied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
              {moderationQueueId ? <RejectAndUpdatePlaybook moderationQueueId={moderationQueueId} /> : null}
            </div>
          </div>
        ) : null}

        {isAutoResponded ? (
          <div className="mx-4  rounded-lg bg-success/5 px-4 py-3 mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-success font-medium">
              <Check className="size-4" />
              Agent auto-responded on {card.platform}
            </div>
            {card.aiDraft ? <p className="text-xs text-muted-foreground truncate max-w-xs">{card.aiDraft?.slice(0, 60)}…</p> : null}
          </div>
        ) : null}

        {isFlagged ? (
          <div className="flex items-center gap-2 px-4">
            {!showAiDraftPanel ? (
              <Button
                type="button"
                size="sm"
                className="rounded-md text-xs bg-foreground text-background gap-1.5"
                disabled={generateResponsePending || !flagItem}
                onClick={handleGenerateFlaggedResponse}
              >
                {generateResponsePending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                Generate Response
              </Button>
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-md text-xs border-warning/30 text-warning gap-1.5">
                <EyeOff className="size-4" /> Hide
              </Button>

              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-md text-xs border-destructive/30 text-destructive gap-1.5"
                disabled={removeQueueItemPending || !flagItem?.id}
                onClick={() => {
                  if (flagItem?.id) removeQueueItem(flagItem.id);
                }}
              >
                {removeQueueItemPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                {removeQueueItemPending ? "Removing…" : "Delete"}
              </Button>
              {/* <Button size="sm" variant="outline" className="rounded-md text-xs">
                Dismiss
              </Button> */}
            </div>
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-2 mt-5 w-max pr-4">
            <Button size="sm" variant="outline" className="rounded-md text-xs border-warning/30 text-warning gap-1.5">
              <EyeOff className="size-4" /> Hide
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-md text-xs border-destructive/30 text-destructive gap-1.5"
              disabled={removeQueueItemPending || !flagItem?.id}
              onClick={() => {
                if (flagItem?.id) removeQueueItem(flagItem.id);
              }}
            >
              {removeQueueItemPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {removeQueueItemPending ? "Removing…" : "Delete"}
            </Button>
            {/* <Button size="sm" variant="outline" className="rounded-md text-xs">
              Dismiss
            </Button> */}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
